import { BaseQueryFn, FetchArgs, FetchBaseQueryMeta, fetchBaseQuery } from "@reduxjs/toolkit/query";
import { ApiError, normalizeApiError } from "../../utils/errorUtils";
import qs from "qs";
import { appConfig } from "../../appConfig";
import { isEndpointInList } from "../../utils/pathUtils";
import { Mutex } from "async-mutex";
import { refreshManager } from "./refreshManager";

const refreshMutex = new Mutex();

export const createAuthBaseQuery = (baseUrl: string): BaseQueryFn<string | FetchArgs, unknown, ApiError, {}, FetchBaseQueryMeta> => {
  const baseQuery = fetchBaseQuery({
    baseUrl: baseUrl,
    credentials: "include",
    paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "repeat" }),
  });

  return async (args, api, extraOptions) => {
    await refreshMutex.waitForUnlock();

    let result = await baseQuery(args, api, extraOptions);

    if (!result.error) return result;

    const normalizedError = normalizeApiError(result.error);
    const requestUrl = typeof args === "string" ? args : args.url;

    if (normalizedError.status !== 401) {
      return { error: normalizedError, meta: result.meta };
    }

    // Handle 401 Unauthorized errors

    const skipRefresh = isEndpointInList(requestUrl, appConfig.authEndpoints);

    if (skipRefresh) {
      return { error: normalizedError, meta: result.meta };
    }

    // only one refresh at a time
    if (!refreshMutex.isLocked()) {
      const release = await refreshMutex.acquire();
      try {
        const ok = await refreshManager.refresh(fetch);

        if (!ok) {
          return {
            error: {
              ...normalizedError,
              status: 403,
              errorCode: "REFRESH_FAILED",
              globalErrors: ["errors.REFRESH_FAILED"],
            } as ApiError,
            meta: result.meta,
          };
        }

        // Retry original request after successful refresh
        result = await baseQuery(args, api, extraOptions);

        return result.error ? { error: normalizeApiError(result.error), meta: result.meta } : { data: result.data, meta: result.meta };
      } finally {
        release();
      }
    }

    // wait until refresh completes
    await refreshMutex.waitForUnlock();

    // retry request
    result = await baseQuery(args, api, extraOptions);

    return result.error ? { error: normalizeApiError(result.error), meta: result.meta } : { data: result.data, meta: result.meta };
  };
};
